import React, { useEffect, useRef, useState } from 'react';
import Tag from '../atoms/Tag';
import FieldLabel from '../atoms/FieldLabel';

/**
 * Multi-select: dropdown with checkboxes + removable tags.
 * The dropdown closes on any click outside the field, or on Escape.
 */
export default function RoleMultiSelect({
  label = '',
  options = [],
  value = [],
  onChange,
  placeholder = 'Select roles',
  className = '',
  hasError = false,
  required = false,
}) {
  const [open, setOpen] = useState(false);
  // Ref is on the field + menu only (not the label/tags), so any click outside closes it.
  const fieldRef = useRef(null);

  const normalizedOptions = options.map((opt) => (
    typeof opt === 'string'
      ? { value: opt, label: opt }
      : { value: opt.value, label: opt.label }
  ));

  const optionMap = new Map(
    normalizedOptions.map((opt) => [String(opt.value), opt.label]),
  );

  const isSelected = (optionValue) => value.some(
    (selectedValue) => String(selectedValue) === String(optionValue),
  );

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (fieldRef.current && !fieldRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleToggle = (optionValue) => {
    if (isSelected(optionValue)) {
      onChange?.(value.filter((r) => String(r) !== String(optionValue)));
    } else {
      onChange?.([...value, optionValue]);
    }
  };

  const handleRemove = (role) => {
    onChange?.(value.filter((r) => String(r) !== String(role)));
  };

  const summary = value.length ? `${value.length} selected` : placeholder;

  return (
    <div
      className={`ca-role-select ${className}`.trim()}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 8,
        width: '100%',
      }}
    >
      <FieldLabel>
        {label}
        {required ? (
          <span style={{ color: '#dc2626', marginLeft: 2 }} aria-hidden="true">*</span>
        ) : null}
      </FieldLabel>

      <div
        className="ca-multi-select"
        ref={fieldRef}
        style={{ position: 'relative', width: '100%' }}
      >
        <button
          type="button"
          className={`ca-multi-select__trigger ${hasError ? 'ca-control--invalid' : ''}`.trim()}
          style={{
            width: '100%',
            border: hasError ? '1px solid #dc2626' : undefined,
            boxShadow: hasError ? '0 0 0 3px rgba(220, 38, 38, 0.12)' : undefined,
          }}
          onClick={() => setOpen((current) => !current)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={value.length ? '' : 'ca-multi-select__placeholder'}>{summary}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {open ? (
          <ul
            className="ca-multi-select__menu"
            role="listbox"
            aria-multiselectable="true"
            style={{
              position: 'absolute',
              top: 'auto',
              bottom: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              zIndex: 20,
            }}
          >
            {normalizedOptions.length ? normalizedOptions.map((opt) => (
              <li key={String(opt.value)} role="option" aria-selected={isSelected(opt.value)}>
                <label className="ca-multi-select__option">
                  <input
                    type="checkbox"
                    checked={isSelected(opt.value)}
                    onChange={() => handleToggle(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              </li>
            )) : (
              <li className="ca-multi-select__empty">No roles available</li>
            )}
          </ul>
        ) : null}
      </div>

      {value.length > 0 ? (
        <div className="ca-tag-list">
          {[...new Set(value)].map((role) => (
            <Tag
              key={String(role)}
              label={optionMap.get(String(role)) || String(role)}
              onRemove={() => handleRemove(role)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}